/*-- npm packages --*/
import React from "react";
import { Accordion } from "react-accessible-accordion";

/*-- component molecules --*/
import AccorionItemComponent from "./AccorionItemComponent.molecule";

/*-- interfaces --*/
import { NavItem } from "../../../interfaces/Navigation.interface";

/*-- assets --*/
import Close from "../../../assets/svgs/Close.svg";
import Search from "../../../assets/svgs/Search.svg";
import { StoryblokLink } from "../../../interfaces/Storyblok/StoryblokLink.interface";
import Button from "../../atoms/Button/Button.component";
import { ButtonTypes } from "../../../interfaces/enums/ButtonTypes.enum";
import { ButtonSizeTypes } from "../../../interfaces/enums/ButtonSizeTypes.enum";
import SocialLink from "../../atoms/SocialLink/SocialLink.atom";
import { StoryblokNode } from "../../../interfaces/Storyblok/StoryblokNode.interface";
import VenueIndexSearchBar from "../VenueIndexSearchBar/VenueIndexSearchBar.molecule";

interface SideNavProps {
  pre_navigation_links: NavItem[];
  login_link: StoryblokLink;
  main_navigation_links: NavItem[];
  isActive: boolean;
  toggleSideNav: (state: boolean) => void;
  social_links: StoryblokNode[];
  is_find_us_search_enabled: boolean;
  find_us_link: StoryblokLink;
  find_us_placeholder: string;
  find_us_button_label: string;
  pre_selected_filter_options?: string[];
}
const SideNav: React.FC<SideNavProps> = ({
  pre_navigation_links,
  login_link,
  main_navigation_links,
  isActive,
  toggleSideNav,
  social_links,
  is_find_us_search_enabled,
  find_us_link,
  find_us_placeholder,
  find_us_button_label,
  pre_selected_filter_options = [],
}) => {
  return (
    <div
      className={`flex transition-all duration-[500ms] ease-in-out fixed h-screen w-screen top-0 z-50 lg:hidden ${
        isActive ? "right-0" : "-right-full"
      }`}
    >
      {isActive && <button className="flex-1" aria-label="close" onClick={() => toggleSideNav(false)}></button>}
      <nav
        className={`bg-White lg:hidden overflow-hidden drop-shadow-sm shadow-[#000] flex flex-col ${
          isActive ? "opacity-100 w-full sm:w-96" : "opacity-0 w-0"
        }`}
        aria-label="Side navigation"
      >
        <div className="flex justify-between items-center py-3 px-4">
          <div className="flex justify-start items-center">
            <p className="text-lg font-bold text-Primary-900">Menu</p>
            <div className="px-6 mt-auto">
              {login_link?.cached_url && (
                <Button
                  link={login_link.cached_url}
                  label={"LOGIN"}
                  type={ButtonTypes.PRIMARY}
                  size={ButtonSizeTypes.DEFAULT}
                  className="bg-Secondary-100 hover:bg-Secondary-200 w-full"
                />
              )}
            </div>
          </div>
          <button aria-label="close" onClick={() => toggleSideNav(false)}>
            <Close className="text-Primary-900" />
          </button>
        </div>

        <div className="bg-sherpa-red-main bg-SP6">
          <div className="flex justify-between items-center py-3 px-4">
            <input
              type="text"
              placeholder="Search..."
              className="bg-sherpa-red-main bg-SP6 text-White placeholder-White focus:ring-0 focus:outline-none w-full pr-4"
            />
            <button aria-label="search">
              <Search className="text-White" />
            </button>
          </div>
        </div>
        <div className="overflow-y-scroll h-full flex flex-col">
          <Accordion allowZeroExpanded className="divide-y divide-Body-Disabled">
            <div>
              {main_navigation_links &&
                !!main_navigation_links.length &&
                main_navigation_links.map(
                  link =>
                    link.display_name && <AccorionItemComponent textColor={"primary"} key={link._uid} link={link} />,
                )}
            </div>
            <div>
              {pre_navigation_links &&
                !!pre_navigation_links.length &&
                pre_navigation_links.map(
                  link => link.display_name && <AccorionItemComponent key={link._uid} link={link} />,
                )}
            </div>
            {is_find_us_search_enabled && (
              <div className="px-4 py-4">
                <VenueIndexSearchBar
                  preSelectedFilters={pre_selected_filter_options}
                  placeholder_text={find_us_placeholder}
                  button={[{ link: find_us_link, label: find_us_button_label }]}
                  type={"small"}
                  btnId={"findClubsBtnSideNav"}
                ></VenueIndexSearchBar>
              </div>
            )}
          </Accordion>

          <div className="flex items-center justify-center space-x-4">
            {social_links && !!social_links.length && (
              <div className="flex justify-center items-center flex-wrap gap-x-2 md:w-full">
                {social_links.map((social_link, i) => (
                  <SocialLink
                    key={social_link.content._uid}
                    type={social_link.content.type}
                    url={social_link.content.url}
                    size="lg"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default SideNav;
